/**
 * PLANNER-01 (Seth 2026-08-26: "Map & Route is a COMPLETE mess… complete
 * relook, built from ground up") — the data layer for the Tune-Up Planner.
 *
 * The old Route & Batch view auto-clustered an audience into "Day batch 3"
 * cards that lived only in memory: change a filter, touch the calls-per-day
 * knob, or refresh the page and every tech/date pick evaporated. The rebuild
 * makes the DAY PLAN a real saved thing (pegasus_visit_day + its stops):
 * the dispatcher builds days, Pegasus assists, and nothing disappears.
 *
 * Booking a stop is REAL scheduling (SCHED-01): it creates a Pegasus
 * dispatch with the day's date/tech — it lands on the Board, and the
 * appt_confirm automation queues the customer's confirmation text.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type StopStatus = "to_call" | "no_answer" | "requested" | "booked" | "skipped";

export interface VisitDayStop {
  id: string;
  day_id: string;
  position: number;
  custno: string;
  locno: string;
  name: string | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  phone: string | null;
  email: string | null;
  lat: number | null;
  lng: number | null;
  context: string | null;
  status: StopStatus;
  dispatch_id: string | null;
  booked_date: string | null;
  booked_window: string | null;
  note: string | null;
  /** PLANNER-02: the season visit this stop works (null for segment members). */
  visit_id: string | null;
  /** The arrival window the route gave this stop ("8-12", "10-2", "12-4", "3-7"). */
  arrival_window: string | null;
  /** Estimated start hour on the route (8.0 … 17.0). */
  eta_hour: number | null;
  /** Why it was picked: picked | renewal | at_risk | null. */
  priority: string | null;
  /** The dispatcher set the window by hand — re-sequencing keeps it. */
  window_locked: boolean;
}

/** PLANNER-02: where a day plan came from. */
export type DayOrigin = "manual" | "fill" | "loop" | "proposal";
export type DayStatus = "proposed" | "planning" | "ready" | "done" | "skipped";

export interface VisitDay {
  id: string;
  title: string;
  plan_date: string | null;
  tech_empno: string | null;
  status: DayStatus;
  source_label: string | null;
  notes: string | null;
  created_at: string;
  origin: DayOrigin;
  /** A drawn loop, [[lat, lng], …] (Direction B). */
  area: [number, number][] | null;
  anchor_lat: number | null;
  anchor_lng: number | null;
  /** "Why this day" — written by Fill / the nightly proposals. */
  reason: string | null;
  proposed_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  stops: VisitDayStop[];
}

export const STOP_STATUS_LABEL: Record<StopStatus, string> = {
  to_call: "To call",
  no_answer: "No answer",
  requested: "Wants a date",
  booked: "Booked",
  skipped: "Skipped",
};

const DAY_KEYS = ["visit_days"] as const;

/** Every saved day plan with its stops (position order). */
export function useVisitDays() {
  return useQuery({
    queryKey: [...DAY_KEYS],
    staleTime: 15 * 1000,
    queryFn: async (): Promise<VisitDay[]> => {
      const { data: days, error } = await supabase
        .from("pegasus_visit_day")
        .select("id, title, plan_date, tech_empno, status, source_label, notes, created_at, origin, area, anchor_lat, anchor_lng, reason, proposed_at, approved_at, approved_by")
        .neq("status", "skipped")
        .order("plan_date", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true });
      if (error) throw error;
      const list = (days ?? []) as Omit<VisitDay, "stops">[];
      if (list.length === 0) return [];
      const { data: stops, error: e2 } = await supabase
        .from("pegasus_visit_day_stop")
        .select("*")
        .in("day_id", list.map((d) => d.id))
        .order("position", { ascending: true });
      if (e2) throw e2;
      const byDay = new Map<string, VisitDayStop[]>();
      for (const s of (stops ?? []) as VisitDayStop[]) {
        byDay.set(s.day_id, [...(byDay.get(s.day_id) ?? []), s]);
      }
      return list.map((d) => ({ ...d, stops: byDay.get(d.id) ?? [] }));
    },
  });
}

export function useCreateDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      title: string; plan_date?: string | null; tech_empno?: string | null; source_label?: string | null;
      origin?: DayOrigin; area?: [number, number][] | null; anchor_lat?: number | null; anchor_lng?: number | null; reason?: string | null; status?: DayStatus;
    }): Promise<string> => {
      const { data: u } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("pegasus_visit_day")
        .insert({
          title: v.title,
          plan_date: v.plan_date ?? null,
          tech_empno: v.tech_empno ?? null,
          source_label: v.source_label ?? null,
          origin: v.origin ?? "manual",
          area: v.area ?? null,
          anchor_lat: v.anchor_lat ?? null,
          anchor_lng: v.anchor_lng ?? null,
          reason: v.reason ?? null,
          status: v.status ?? "planning",
          proposed_at: v.status === "proposed" ? new Date().toISOString() : null,
          created_by: u?.user?.email ?? null,
        })
        .select("id")
        .single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [...DAY_KEYS] }); qc.invalidateQueries({ queryKey: ["tech_day_load"] }); qc.invalidateQueries({ queryKey: ["season_visits"] }); },
  });
}

export function useUpdateDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string } & Partial<Pick<VisitDay, "title" | "plan_date" | "tech_empno" | "status" | "notes" | "reason" | "anchor_lat" | "anchor_lng" | "area" | "approved_at" | "approved_by">>) => {
      const { id, ...patch } = v;
      const { error } = await supabase
        .from("pegasus_visit_day")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [...DAY_KEYS] }); qc.invalidateQueries({ queryKey: ["tech_day_load"] }); qc.invalidateQueries({ queryKey: ["season_visits"] }); },
  });
}

/** Delete a day plan — its stops cascade. Booked dispatches are NOT touched
 *  (they're real scheduled visits on the Board; the plan was just the worklist). */
export function useDeleteDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_visit_day").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [...DAY_KEYS] }); qc.invalidateQueries({ queryKey: ["tech_day_load"] }); qc.invalidateQueries({ queryKey: ["season_visits"] }); },
  });
}

export interface NewStop {
  custno: string;
  locno: string;
  name: string | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  phone: string | null;
  email: string | null;
  lat: number | null;
  lng: number | null;
  context: string | null;
  visit_id?: string | null;
  arrival_window?: string | null;
  eta_hour?: number | null;
  priority?: string | null;
  window_locked?: boolean;
}

/** Add members to a day. Upsert on (day_id, custno, locno) so re-adding someone
 *  never duplicates them (first write wins, statuses survive). */
export function useAddStops() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { dayId: string; stops: NewStop[]; startPosition: number }) => {
      if (v.stops.length === 0) return;
      const { data: u } = await supabase.auth.getUser();
      const rows = v.stops.map((s, i) => ({
        ...s,
        day_id: v.dayId,
        position: v.startPosition + i,
        updated_by: u?.user?.email ?? null,
      }));
      const { error } = await supabase
        .from("pegasus_visit_day_stop")
        .upsert(rows, { onConflict: "day_id,custno,locno", ignoreDuplicates: true });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [...DAY_KEYS] }); qc.invalidateQueries({ queryKey: ["tech_day_load"] }); qc.invalidateQueries({ queryKey: ["season_visits"] }); },
  });
}

export function useUpdateStop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string } & Partial<Pick<VisitDayStop, "status" | "note" | "position" | "booked_date" | "booked_window" | "dispatch_id" | "arrival_window" | "eta_hour" | "window_locked">>) => {
      const { id, ...patch } = v;
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("pegasus_visit_day_stop")
        .update({ ...patch, updated_by: u?.user?.email ?? null, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [...DAY_KEYS] }); qc.invalidateQueries({ queryKey: ["tech_day_load"] }); qc.invalidateQueries({ queryKey: ["season_visits"] }); },
  });
}

export function useRemoveStop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_visit_day_stop").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [...DAY_KEYS] }); qc.invalidateQueries({ queryKey: ["tech_day_load"] }); qc.invalidateQueries({ queryKey: ["season_visits"] }); },
  });
}

/** Persist a new drive order for a day's stops (bulk position update). */
export function useReorderStops() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { orderedIds: string[]; windows?: Record<string, { arrival_window: string; eta_hour: number }> }) => {
      for (let i = 0; i < v.orderedIds.length; i++) {
        const w = v.windows?.[v.orderedIds[i]];
        const { error } = await supabase
          .from("pegasus_visit_day_stop")
          .update({ position: i, updated_at: new Date().toISOString(), ...(w ? { arrival_window: w.arrival_window, eta_hour: w.eta_hour } : {}) })
          .eq("id", v.orderedIds[i]);
        if (error) throw error;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [...DAY_KEYS] }); qc.invalidateQueries({ queryKey: ["tech_day_load"] }); qc.invalidateQueries({ queryKey: ["season_visits"] }); },
  });
}

/**
 * BOOK a stop — the real thing, not a proposal. Creates a SCHEDULED Pegasus
 * dispatch (it shows on the Board that day; appt_confirm queues the customer's
 * confirmation text automatically) and stamps the stop booked.
 */
export function useBookStop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      stop: VisitDayStop;
      dayTitle: string;
      date: string;
      window: string | null;
      tech: string | null;
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      const { data: disp, error } = await supabase
        .from("pegasus_dispatch")
        .insert({
          status: "scheduled",
          lane_key: "campaign",
          source: "campaign",
          custno: v.stop.custno,
          locno: v.stop.locno,
          customer_name: v.stop.name ?? v.stop.custno,
          address: v.stop.address,
          city: v.stop.city,
          lat: v.stop.lat,
          lng: v.stop.lng,
          phone: v.stop.phone,
          summary: v.stop.context ?? "Membership tune-up",
          notes: `Booked from the Tune-Up Planner — day plan "${v.dayTitle}".`,
          urgency: "routine",
          assigned_empno: v.tech,
          scheduled_date: v.date,
          scheduled_window: v.window,
          tags: ["tuneup", "planner"],
          created_by: u?.user?.email ?? null,
          created_at: now,
          updated_at: now,
        })
        .select("id")
        .single();
      if (error) throw error;
      const dispId = (disp as { id: string }).id;
      const { error: e2 } = await supabase
        .from("pegasus_visit_day_stop")
        .update({
          status: "booked",
          dispatch_id: dispId,
          booked_date: v.date,
          booked_window: v.window,
          updated_by: u?.user?.email ?? null,
          updated_at: now,
        })
        .eq("id", v.stop.id);
      if (e2) throw e2;
      // PLANNER-02: the season visit follows (owed → booked); the 15-minute
      // reconcile keeps it honest once ESC has the ticket.
      if (v.stop.visit_id) {
        const { error: e3 } = await supabase.rpc("pegasus_visit_book_property", { p_visit: v.stop.visit_id, p_action: "booked", p_when: v.date, p_reason: v.window ? `planner · ${v.window}` : "planner" });
        if (e3) throw e3;
      }
      return dispId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DAY_KEYS] });
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["today"] });
      qc.invalidateQueries({ queryKey: ["season_visits"] });
      qc.invalidateQueries({ queryKey: ["tech_day_load"] });
      qc.invalidateQueries({ queryKey: ["tech_day_calls"] });
    },
  });
}

/** Un-book: cancel the created dispatch + put the stop back to "requested"
 *  (review-before-commit — every planner action has an undo). */
export function useUnbookStop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (stop: VisitDayStop) => {
      const { data: u } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      if (stop.dispatch_id) {
        const { error } = await supabase
          .from("pegasus_dispatch")
          .update({ status: "cancelled", resolved_at: now, resolved_by: u?.user?.email ?? null, updated_at: now })
          .eq("id", stop.dispatch_id);
        if (error) throw error;
      }
      const { error: e2 } = await supabase
        .from("pegasus_visit_day_stop")
        .update({ status: "to_call", dispatch_id: null, booked_date: null, booked_window: null, updated_by: u?.user?.email ?? null, updated_at: now })
        .eq("id", stop.id);
      if (e2) throw e2;
      if (stop.visit_id) {
        const { error: e3 } = await supabase.rpc("pegasus_visit_book_property", { p_visit: stop.visit_id, p_action: "reopen", p_when: null, p_reason: "un-booked from the planner" });
        if (e3) throw e3;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DAY_KEYS] });
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
      qc.invalidateQueries({ queryKey: ["season_visits"] });
      qc.invalidateQueries({ queryKey: ["tech_day_load"] });
      qc.invalidateQueries({ queryKey: ["tech_day_calls"] });
    },
  });
}

/** Customers in the audience who ASKED ONLINE (open portal requests) — the
 *  planner marks them so the dispatcher calls the raised hands first. */
export function usePortalRequestFlags() {
  return useQuery({
    queryKey: ["planner_portal_requests"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Map<string, string>> => {
      const { data, error } = await supabase
        .from("portal_request")
        .select("custno, preferred_time, created_at")
        .eq("status", "new")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      const m = new Map<string, string>();
      for (const r of (data ?? []) as { custno: string; preferred_time: string | null }[]) {
        if (!m.has(r.custno)) m.set(r.custno, r.preferred_time ?? "asked online");
      }
      return m;
    },
  });
}

/** PLANNER-02 C — the nightly proposals (also on demand) and the decision on one. */
export function useProposeNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (days: number): Promise<{ proposed_days: number; proposed_stops: number; per_day: number; skipped_no_room: number; skipped_no_members: number } | null> => {
      const { data, error } = await supabase.rpc("pegasus_planner_propose", { p_days: days });
      if (error) throw error;
      return ((data ?? []) as { proposed_days: number; proposed_stops: number; per_day: number; skipped_no_room: number; skipped_no_members: number }[])[0] ?? null;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DAY_KEYS] });
      qc.invalidateQueries({ queryKey: ["tech_day_load"] });
      qc.invalidateQueries({ queryKey: ["season_visits"] });
    },
  });
}

export function useDecideProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { dayId: string; action: "approve" | "skip" }) => {
      const { error } = await supabase.rpc("pegasus_planner_decide", { p_day: v.dayId, p_action: v.action });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...DAY_KEYS] });
      qc.invalidateQueries({ queryKey: ["tech_day_load"] });
      qc.invalidateQueries({ queryKey: ["season_visits"] });
    },
  });
}
